/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Turns the wizard's form model into one batch payload for
 * POST /arcee/application/table/save/records/.
 *
 * The endpoint writes every operation in a single transaction, so an event and
 * all of its children exist together or not at all. Order is significant: a
 * parent must be written before any child that names it with `@ref`.
 */

import { wallClockToUtcIso } from '../../utils/zonedDateTime';

export const EVENT_REF = 'e';

/**
 * The datetime columns an admin types as a wall clock, per table.
 *
 * Every one of them is entered through `<input type="datetime-local">`, which
 * yields `YYYY-MM-DDTHH:MM` with no zone attached. They are converted against
 * `form.timezone` on the way out so the column holds the instant the admin
 * meant — see `zonedDateTime.js`.
 *
 * `published_at` is deliberately absent: it is `new Date().toISOString()`, an
 * instant the browser already knows, never a wall clock anybody typed.
 */
const WALL_CLOCK_COLUMNS = {
  event: ['start_datetime', 'end_datetime'],
  platform: ['registration_start_datetime', 'registration_end_datetime'],
  external: ['registration_start_date', 'registration_end_date'],
};

/**
 * Converts the named columns of one already-picked row from wall clock to UTC.
 *
 * Returns a new object; a row with no timezone, or none of these columns, comes
 * back byte-for-byte the same.
 */
function withUtcDatetimes(data, columns, timeZone) {
  const out = { ...data };
  columns.forEach((column) => {
    if (out[column] !== undefined) {
      out[column] = wallClockToUtcIso(out[column], timeZone);
    }
  });
  return out;
}

/** Columns that live directly on `event`, in the order the table declares them. */
const EVENT_COLUMNS = [
  'creator_type', 'creator_user_id', 'creator_organization_id', 'organizer',
  'activity_mode', 'number_of_activities', 'title', 'slug', 'description',
  'prerequisites', 'preparation_steps',
  'cover_image_url', 'official_website_url', 'location', 'location_state',
  'location_country', 'location_pincode', 'timezone',
  'start_datetime', 'end_datetime', 'registration_source', 'registration_type_id',
  'is_registration_required', 'is_payment_required', 'status',
];

/** Optional event columns an admin may deliberately empty on an edit. */
const CLEARABLE_EVENT_COLUMNS = ['prerequisites', 'preparation_steps'];

/**
 * batch_save refuses an operation whose list mixes rows that carry an id with
 * rows that do not — one operation is uniformly an insert or an update. So a
 * list of children that has both is emitted as two operations.
 */
function listOperations(table, rows) {
  const existing = rows.filter((row) => row.id !== undefined && row.id !== null && row.id !== '');
  const created = rows.filter((row) => row.id === undefined || row.id === null || row.id === '');
  const operations = [];
  if (existing.length) operations.push({ table, data: existing });
  if (created.length) operations.push({ table, data: created.map(({ id, ...rest }) => rest) });
  return operations;
}

/** Adds `id` when the row already exists, so the batch updates it in place. */
function withId(source, data) {
  const id = source?.id;
  return (id === undefined || id === null || id === '') ? data : { ...data, id };
}

function pick(source, columns) {
  const out = {};
  columns.forEach((column) => {
    const value = source[column];
    if (value !== undefined && value !== null && value !== '') out[column] = value;
  });
  return out;
}

const ACTIVITY_COLUMNS = ['activity_type_id', 'name', 'description', 'location_model'];
const ROUTE_COLUMNS = [
  'start_point', 'start_address', 'end_point', 'end_address',
  'route_polyline', 'gpx_file_url', 'total_distance', 'distance_measuring_unit_id',
];
const VENUE_COLUMNS = ['venue_name', 'venue_address', 'venue_point'];
const PIT_STOP_COLUMNS = ['name', 'description', 'point'];
const SPONSOR_COLUMNS = ['name', 'logo_url', 'cover_image', 'website_url'];
// refund_policy is deliberately absent: refunds are part of the deferred
// payments phase, so the column stays in the table and empty in the UI.
const PLATFORM_REGISTRATION_COLUMNS = [
  'registration_start_datetime', 'registration_end_datetime', 'max_participants',
];
const EXTERNAL_REGISTRATION_COLUMNS = [
  'external_url', 'registration_start_date', 'registration_end_date',
];

/**
 * The only two states an admin may author. Everything beyond them —
 * REGISTRATION_OPEN, REGISTRATION_CLOSED, COMPLETED — is owned by the
 * platform's scheduled transitions and moves on a clock. Letting the form set
 * one would announce a registration the backend has not actually opened.
 */
export const AUTHORABLE_STATUSES = ['DRAFT', 'PUBLISHED'];

export function buildEventBatch(form, { organizationId, applicationId }) {
  const activities = form.activities || [];

  if (form.status && !AUTHORABLE_STATUSES.includes(form.status)) {
    throw new Error(
      `status must be one of ${AUTHORABLE_STATUSES.join(' or ')} — `
      + `"${form.status}" is set by the platform when its scheduled time arrives.`
    );
  }

  // The zone every wall clock in this form is read against. Required by the
  // form, so in practice always present; when it is not, every conversion below
  // passes its value through and the payload is exactly what it was before.
  const timeZone = form.timezone;

  const eventData = withUtcDatetimes(
    pick(form, EVENT_COLUMNS), WALL_CLOCK_COLUMNS.event, timeZone,
  );

  // chk_event_creator_xor allows exactly one creator column. A form that has
  // held both — the admin picked an organization, then switched to USER —
  // would otherwise send both and be refused by the database.
  if (form.creator_type === 'USER') {
    delete eventData.creator_organization_id;
  } else {
    delete eventData.creator_user_id;
  }
  // Derived, never taken from input: the count must match the rows actually
  // written, or the event advertises categories that do not exist.
  eventData.number_of_activities = activities.length;

  // `pick` drops empty strings, which is right on create — an untouched
  // optional column should stay NULL rather than be written as ''. On an edit
  // it means the opposite of what the admin did: clearing the box sends
  // nothing, so the old text survives the save. These two are sent as an
  // explicit null instead, so an emptied field is actually emptied. Scoped to
  // the columns this form can clear on purpose; the rest keep their existing
  // behaviour.
  if (form.id !== undefined && form.id !== null && form.id !== '') {
    CLEARABLE_EVENT_COLUMNS.forEach((column) => {
      if (eventData[column] === undefined) eventData[column] = null;
    });
  }

  // Publishing is the moment the event becomes visible, so record it here.
  // A draft has never been published and must leave the column empty.
  if (form.status === 'PUBLISHED') {
    eventData.published_at = new Date().toISOString();
  }

  // An event that already exists is referenced by its real id; only a new one
  // needs a @ref for its children to point at.
  const isExisting = form.id !== undefined && form.id !== null && form.id !== '';
  const eventKey = isExisting ? form.id : `@${EVENT_REF}`;

  const operations = [
    isExisting
      ? { table: 'event', data: { ...eventData, id: form.id } }
      : { ref: EVENT_REF, table: 'event', data: eventData },
  ];

  activities.forEach((activity, index) => {
    const activityRef = `a${index}`;
    const activityExists = activity.id !== undefined && activity.id !== null && activity.id !== '';
    const activityKey = activityExists ? activity.id : `@${activityRef}`;
    const activityData = {
      ...pick(activity, ACTIVITY_COLUMNS),
      event_id: eventKey,
      order_index: index,
    };
    operations.push(
      activityExists
        ? { table: 'event_activity', data: { ...activityData, id: activity.id } }
        : { ref: activityRef, table: 'event_activity', data: activityData }
    );

    if (activity.location_model === 'ROUTE_BASED' && activity.route) {
      operations.push({
        table: 'event_activity_route',
        data: withId(activity.route, {
          ...pick(activity.route, ROUTE_COLUMNS),
          event_activity_id: activityKey,
        }),
      });
    }

    if (activity.location_model === 'VENUE_BASED' && activity.venue) {
      operations.push({
        table: 'event_activity_venue',
        data: withId(activity.venue, {
          ...pick(activity.venue, VENUE_COLUMNS),
          event_activity_id: activityKey,
        }),
      });
    }

    // One operation carrying every stop, rather than one per stop: the batch
    // caps operations at 50, and a long route would otherwise exhaust it.
    const pitStops = activity.pit_stops || [];
    if (pitStops.length) {
      operations.push(...listOperations('event_activity_pit_stop', pitStops.map((stop, stopIndex) => ({
        ...(stop.id ? { id: stop.id } : {}),
        ...pick(stop, PIT_STOP_COLUMNS),
        event_activity_id: activityKey,
        order_index: stopIndex,
      }))));
    }
  });

  if (form.registration_source === 'PLATFORM' && form.platform_registration) {
    operations.push({
      table: 'event_platform_registration',
      data: withId(form.platform_registration, {
        ...withUtcDatetimes(
          pick(form.platform_registration, PLATFORM_REGISTRATION_COLUMNS),
          WALL_CLOCK_COLUMNS.platform, timeZone,
        ),
        event_id: eventKey,
        // Free registration this phase. The column is required, and pinning it
        // here means enabling payments later relaxes a value rather than adding
        // a required column to live rows.
        fee_amount: 0,
      }),
    });
  }

  if (form.registration_source === 'EXTERNAL' && form.external_registration) {
    operations.push({
      table: 'event_external_registration',
      data: withId(form.external_registration, {
        ...withUtcDatetimes(
          pick(form.external_registration, EXTERNAL_REGISTRATION_COLUMNS),
          WALL_CLOCK_COLUMNS.external, timeZone,
        ),
        event_id: eventKey,
      }),
    });
  }

  const sponsors = form.sponsors || [];
  if (sponsors.length) {
    operations.push(...listOperations('event_sponsor', sponsors.map((sponsor, sponsorIndex) => ({
      ...(sponsor.id ? { id: sponsor.id } : {}),
      ...pick(sponsor, SPONSOR_COLUMNS),
      event_id: eventKey,
      order_index: sponsorIndex,
    }))));
  }

  return {
    organization_id: organizationId,
    application_id: applicationId,
    operations,
  };
}
